
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Security best practices',
  layout: 'guide',
  description: 'Integrate with Plaid securely',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Security best practices' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "ip-allowlist"
      }}>{`IP allowlist`}</h2>
      <p>{`Allowlist Plaid's IP addresses in your firewall, WAF, or security vendor configuration to prevent legitimate API traffic from being blocked. This ensures scheduled balance and transaction updates run smoothly.`}</p>
      <p><strong parentName="p">{`Add these IP addresses:`}</strong></p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`162.120.88.0/22`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`2602:F74B::/40`}</inlineCode></li>
      </ul>
      <Callout warning mdxType="Callout">
        <p><strong parentName="p">{`Do not IP-restrict your discovery or JWKS endpoints.`}</strong>{` `}<inlineCode parentName="p">{`/.well-known/openid-configuration`}</inlineCode>{` and `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` must remain reachable from the public internet. Plaid fetches them from infrastructure that is not part of the allowlisted ranges, and restricting them will cause Validator end-to-end runs to fail with `}<inlineCode parentName="p">{`Error getting OIDC configuration`}</inlineCode>{` even when the rest of the integration is correct. The allowlist should be applied only to your `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{`, `}<inlineCode parentName="p">{`userinfo_endpoint`}</inlineCode>{`, and Core Exchange data endpoints.`}</p>
      </Callout>
      <p><strong parentName="p">{`If already present, do not remove:`}</strong></p>
      <ul>
        <li parentName="ul">{`18.214.218.91`}</li>
        <li parentName="ul">{`3.211.30.208`}</li>
        <li parentName="ul">{`3.214.25.67`}</li>
        <li parentName="ul">{`54.88.74.128`}</li>
        <li parentName="ul">{`54.208.59.10`}</li>
        <li parentName="ul">{`54.88.202.28`}</li>
        <li parentName="ul">{`34.199.37.46`}</li>
        <li parentName="ul">{`35.168.137.48`}</li>
        <li parentName="ul">{`3.215.49.214`}</li>
        <li parentName="ul">{`34.202.178.138`}</li>
        <li parentName="ul">{`52.0.205.192`}</li>
        <li parentName="ul">{`52.3.166.211`}</li>
        <li parentName="ul">{`35.174.147.86`}</li>
        <li parentName="ul">{`52.88.82.239`}</li>
        <li parentName="ul">{`52.41.247.19`}</li>
        <li parentName="ul">{`3.233.249.56`}</li>
        <li parentName="ul">{`35.153.85.253`}</li>
        <li parentName="ul">{`3.219.116.195`}</li>
      </ul>
      <p><strong parentName="p">{`Implementation:`}</strong></p>
      <ul>
        <li parentName="ul">{`Notify your Plaid contact once allowlisting is complete.`}</li>
        <li parentName="ul">{`Allowlisting does not bypass authentication. Users still complete login and MFA on first connection.`}</li>
        <li parentName="ul">{`Scheduled refreshes can proceed without repeated MFA challenges.`}</li>
      </ul>
      <h2 {...{
        "id": "security-layers"
      }}>{`Security layers`}</h2>
      <p>{`Core Exchange implements security in layers. Each layer reinforces the others:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Transport:`}</strong>{` TLS 1.2+, mTLS for Production`}</li>
        <li parentName="ul"><strong parentName="li">{`Authentication:`}</strong>{` Client credentials, token-based access`}</li>
        <li parentName="ul"><strong parentName="li">{`Authorization:`}</strong>{` Scoped tokens, user consent`}</li>
        <li parentName="ul"><strong parentName="li">{`Validation:`}</strong>{` JWKS signature verification, PKCE`}</li>
        <li parentName="ul"><strong parentName="li">{`Lifecycle:`}</strong>{` Token expiration, refresh rotation, key rotation`}</li>
      </ul>
      <p><strong parentName="p">{`Defense in depth:`}</strong>{` If one layer is compromised, the others still protect the system.`}</p>
      <h2 {...{
        "id": "quick-security-wins"
      }}>{`Quick security wins`}</h2>
      <p>{`Implement these high-impact practices first:`}</p>
      <h3 {...{
        "id": "plaid-enforces"
      }}>{`Plaid enforces`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Use HTTPS everywhere`}</strong>{` - All OAuth endpoints must use HTTPS with valid certificates. Plaid validates TLS and blocks self-signed certificates in Production.`}</li>
        <li parentName="ul"><strong parentName="li">{`Expire access tokens in 15 min`}</strong>{` - Plaid automatically refreshes expired tokens. If you expire a token early, return a 401 with error code "602 not authorized".`}</li>
      </ul>
      <h3 {...{
        "id": "strongly-recommended"
      }}>{`Strongly recommended`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Enable mTLS in Production`}</strong>{` - Mutual authentication prevents client impersonation. Coordinate with your Plaid contact to configure.`}</li>
        <li parentName="ul"><strong parentName="li">{`Use PKCE for mobile`}</strong>{` - Prevents authorization code interception on public clients. Include `}<inlineCode parentName="li">{`code_challenge`}</inlineCode>{` and `}<inlineCode parentName="li">{`code_challenge_method`}</inlineCode>{` parameters.`}</li>
        <li parentName="ul"><strong parentName="li">{`Rotate keys annually`}</strong>{` - Or immediately if compromised. Schedule during low-traffic periods. Plaid caches JWKS for up to 10 minutes.`}</li>
        <li parentName="ul"><strong parentName="li">{`Encrypt client secrets`}</strong>{` - Never store in plain text. Use secrets manager or vault.`}</li>
      </ul>
      <p>{`These six practices address 80% of common security risks. Then implement the complete checklist below.`}</p>
      <h2 {...{
        "id": "transport-security"
      }}>{`Transport security`}</h2>
      <h3 {...{
        "id": "tls-12-required"
      }}>{`TLS 1.2+ required`}</h3>
      <p>{`All OAuth endpoints must use TLS 1.2 or higher. Non-production environments (for example, Development or Staging) accept self-signed certificates, but Production requires CA-signed certificates.`}</p>
      <h3 {...{
        "id": "mtls-mutual-tls"
      }}>{`mTLS (mutual TLS)`}</h3>
      <p>{`Many Data Providers require mutual TLS for server-to-server token endpoint calls. This adds certificate-based authentication on top of client credentials, providing an additional security layer.`}</p>
      <p><strong parentName="p">{`When you use mTLS:`}</strong></p>
      <ul>
        <li parentName="ul">{`Plaid presents a client certificate during the TLS handshake.`}</li>
        <li parentName="ul">{`Your server validates the certificate against a trust store.`}</li>
        <li parentName="ul">{`The certificate identifies Plaid as the connecting client.`}</li>
      </ul>
      <p><strong parentName="p">{`Certificate management:`}</strong></p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Aspect`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Details`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Certificate generation`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Plaid can generate certificates or use DP-provided certificates based on your requirements.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Certificate Authority`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Data Providers may specify the required CAs (e.g., DigiCert, Let's Encrypt).`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Certificate fields`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`CN (Common Name) typically follows the pattern: `}<inlineCode parentName="td">{`plaid-mtls-<shortname>.plaid.com`}</inlineCode>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Validity period`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Usually 1 year, coordinate renewal at least 2 weeks before expiration.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Trust store`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The Data Provider maintains a trust store containing Plaid's certificate (or the issuing CA).`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "certificate-rotation-process"
      }}>{`Certificate rotation process`}</h3>
      <ol>
        <li parentName="ol"><strong parentName="li">{`Plaid initiates rotation`}</strong>{` - Plaid monitors certificate expiration and notifies you at least 2 weeks before expiration`}</li>
        <li parentName="ol"><strong parentName="li">{`Generate a new certificate`}</strong>{` - Plaid generates a new certificate with updated expiration and provides it to you`}</li>
        <li parentName="ol"><strong parentName="li">{`Update trust store`}</strong>{` - Add new certificate to your trust store (keep old cert active during transition)`}</li>
        <li parentName="ol"><strong parentName="li">{`Testing window`}</strong>{` - Allow 48-72 hours for testing with the new certificate`}</li>
        <li parentName="ol"><strong parentName="li">{`Remove old certificate`}</strong>{` - After confirming success, remove the expired certificate from the trust store`}</li>
      </ol>
      <p><strong parentName="p">{`Best practices:`}</strong></p>
      <ul>
        <li parentName="ul">{`Set up alerts 30 days before certificate expiration.`}</li>
        <li parentName="ul">{`Test certificate rotation in a non-production environment before Production.`}</li>
        <li parentName="ul">{`Maintain both old and new certificates during the transition period.`}</li>
        <li parentName="ul">{`Document certificate details (CN, expiration, issuer) for reference.`}</li>
      </ul>
      <h2 {...{
        "id": "credential-management"
      }}>{`Credential management`}</h2>
      <p><strong parentName="p">{`Encrypt at rest`}</strong>{` - Never store client secrets in plaintext. Use a secrets manager (AWS Secrets Manager, HashiCorp Vault, etc.) or, at a minimum, encrypt them in your database.`}</p>
      <p><strong parentName="p">{`Secure generation`}</strong>{` - Use a cryptographically secure pseudo-random number generator (CSPRNG) for all secrets. Don't use UUIDs or predictable values.`}</p>
      <h2 {...{
        "id": "token-security"
      }}>{`Token security`}</h2>
      <p><strong parentName="p">{`Short-lived access tokens`}</strong>{` - 15 minutes (900 seconds) is short enough to limit exposure, long enough to avoid excessive refresh calls.`}</p>
      <p><strong parentName="p">{`Refresh token rotation`}</strong>{` - Issue new refresh tokens with each use. This limits the window if a refresh token is compromised.`}</p>
      <p><strong parentName="p">{`Revocation support`}</strong>{` - Build a way to revoke tokens when users disconnect or when you detect suspicious activity.`}</p>
      <h2 {...{
        "id": "pkce-for-public-clients"
      }}>{`PKCE for public clients`}</h2>
      <p><strong parentName="p">{`Use S256`}</strong>{` - If you support public clients (mobile apps, SPAs), require PKCE with the S256 challenge method. This prevents authorization code interception attacks.`}</p>
      <h2 {...{
        "id": "key-rotation"
      }}>{`Key rotation`}</h2>
      <p><strong parentName="p">{`Annual minimum`}</strong>{` - Rotate JWKS signing keys at least once a year.`}</p>
      <p><strong parentName="p">{`Emergency rotation`}</strong>{` - Have a process ready to rotate keys immediately if a key is compromised. Test it before you need it.`}</p>
      <p><strong parentName="p">{`Grace period`}</strong>{` - When rotating keys, publish new keys before removing old ones, and keep both published for 24 hours. Plaid's JWKS cache expires within 10 minutes, so the longer overlap is a safety margin against in-flight tokens signed with the old key.`}</p>
      <p><strong parentName="p">{`Track key usage`}</strong>{` - Include a unique `}<inlineCode parentName="p">{`kid`}</inlineCode>{` (Key ID) in every key so you can identify which key signed which token.`}</p>
      <h2 {...{
        "id": "additional-security-practices"
      }}>{`Additional security practices`}</h2>
      <ul>
        <li parentName="ul">{`Use `}<inlineCode parentName="li">{`openid`}</inlineCode>{` + `}<inlineCode parentName="li">{`offline_access`}</inlineCode>{` scopes - See `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/oauth-server-setup#well-known-configuration"
          }}>{`OAuth Server Setup`}</a></li>
        <li parentName="ul">{`Log `}<inlineCode parentName="li">{`state`}</inlineCode>{`, `}<inlineCode parentName="li">{`userId`}</inlineCode>{`, and timestamps for every session - See `}<a parentName="li" {...{
            "href": "/core-exchange/docs/troubleshooting"
          }}>{`Troubleshooting`}</a></li>
        <li parentName="ul">{`Monitor OAuth and JWKS validation success rates - See `}<a parentName="li" {...{
            "href": "/core-exchange/docs/troubleshooting"
          }}>{`Troubleshooting`}</a>{` and `}<a parentName="li" {...{
            "href": "/core-exchange/docs/post-launch"
          }}>{`Post-launch operations`}</a></li>
      </ul>
      <h2 {...{
        "id": "security-review-schedule"
      }}>{`Security review schedule`}</h2>
      <p><strong parentName="p">{`Initial implementation:`}</strong>{` Complete full security review before Production launch`}</p>
      <p><strong parentName="p">{`Ongoing:`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Quarterly:`}</strong>{` Review access logs, error patterns, and credential rotation schedule.`}</li>
        <li parentName="ul"><strong parentName="li">{`Annually:`}</strong>{` Rotate JWKS keys, review TLS configuration, and update dependencies.`}</li>
        <li parentName="ul"><strong parentName="li">{`After incidents:`}</strong>{` Review immediately if a security event is detected.`}</li>
      </ul>
      <h2 {...{
        "id": "security-checklist"
      }}>{`Security checklist`}</h2>
      <ul>
        <li parentName="ul">{`HTTPS on all OAuth endpoints with valid CA certificates`}</li>
        <li parentName="ul">{`Access tokens expire in 15 minutes`}</li>
        <li parentName="ul">{`Refresh tokens expire in 13+ months (or rotate on each use)`}</li>
        <li parentName="ul">{`mTLS configured for Production (if required)`}</li>
        <li parentName="ul">{`PKCE enabled for mobile/public clients`}</li>
        <li parentName="ul">{`Client secrets encrypted at rest`}</li>
        <li parentName="ul">{`JWKS key rotation process documented and tested`}</li>
        <li parentName="ul">{`Certificate rotation process documented and tested`}</li>
        <li parentName="ul">{`Security review schedule established`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "ip-allowlist",
        level: 2,
        title: "IP allowlist",
      },
{
        id: "security-layers",
        level: 2,
        title: "Security layers",
      },
{
        id: "quick-security-wins",
        level: 2,
        title: "Quick security wins",
      },
{
        id: "plaid-enforces",
        level: 3,
        title: "Plaid enforces",
      },
{
        id: "strongly-recommended",
        level: 3,
        title: "Strongly recommended",
      },
{
        id: "transport-security",
        level: 2,
        title: "Transport security",
      },
{
        id: "tls-12-required",
        level: 3,
        title: "TLS 1.2+ required",
      },
{
        id: "mtls-mutual-tls",
        level: 3,
        title: "mTLS (mutual TLS)",
      },
{
        id: "certificate-rotation-process",
        level: 3,
        title: "Certificate rotation process",
      },
{
        id: "credential-management",
        level: 2,
        title: "Credential management",
      },
{
        id: "token-security",
        level: 2,
        title: "Token security",
      },
{
        id: "pkce-for-public-clients",
        level: 2,
        title: "PKCE for public clients",
      },
{
        id: "key-rotation",
        level: 2,
        title: "Key rotation",
      },
{
        id: "additional-security-practices",
        level: 2,
        title: "Additional security practices",
      },
{
        id: "security-review-schedule",
        level: 2,
        title: "Security review schedule",
      },
{
        id: "security-checklist",
        level: 2,
        title: "Security checklist",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
